import { useEffect } from "react";
import { Mui } from "@osu/react-ui";
import { useDispatch, useSelector } from "react-redux";
import { resetRubricValues, setRubricValue, selector } from "../slices/rubricValues";
import { useApplicantDetailsState } from "../hooks/useApplicantDetailsState";
import useCommonStyles from "../../Common/hooks/commonStyles";

export default function Rubric() {
    // Route-aware: reads getApplicationDetails (Honors) or getScholarsApplicationDetails (Scholars)
    // depending on the current applicant-detail page. Both queries return configuration.rubric.
    const { data } = useApplicantDetailsState();
    const rubricConfig = data?.configuration?.rubric ?? [];
    const missingConfig = !(rubricConfig?.rubricItems?.length > 0);
    const resumeLaterRubricValues = data?.details?.resumeLaterRubricValues;
    // Honors gates editability on the per-person review assignment. Scholars is queue-based (no person
    // assignment): the Ratings section is only rendered when the applicant is actively IN_REVIEW in a
    // program queue (see scholarsSections customFilter), so on the Scholars route a visible section is
    // editable. Route detection mirrors useApplicantDetailsState.
    const isScholars = typeof window !== "undefined" && window.location.pathname.includes("/scholars/applicant");
    const readOnly = isScholars
        ? false
        : (!data?.status.isAssignedReviewer || data?.status.isSubmittedReviewer);
    const dispatch = useDispatch();
    
    useEffect(() => {
        // Set up rubric slice
        dispatch(resetRubricValues({ rubricConfig, resumeLaterRubricValues }));
        
        // Empty out rubric values when leaving page
        return () => { dispatch(resetRubricValues({ rubricConfig })); };
    }, [JSON.stringify(rubricConfig), resumeLaterRubricValues]);
    
    return (
        <div style={{ margin: "0 1rem" }}>
            { rubricConfig?.rubricItems?.map(item => <RubricItem key={item.label} item={item} readOnly={readOnly} />) }
            <RubricIsRequired missingConfig={missingConfig} />
            
            <MissingRubricConfiguration missingConfig={missingConfig} />
        </div>
    );
}

function RubricItem({ item, readOnly }) {
    const classes = useCommonStyles();
    
    return (
        <Mui.FormControl sx={{ marginBottom: "2rem", width: "50%" }}>
            <Mui.FormLabel id={`${item.label}-radio-selector`} sx={classes.darkLightColor}>{item.label}</Mui.FormLabel>
            <Mui.RadioGroup
                aria-labelledby={`${item.label}-radio-selector`}
                defaultValue=""
                fullwidth="true"
                name={item.label}
            >
                {item.choices.map(choice => <ItemLabelAndRadio key={`radio-${item.label}-${choice.label}`} choice={choice} rubricItem={item.label} readOnly={readOnly} />)}
            </Mui.RadioGroup>
        </Mui.FormControl>
    );
}

function ItemLabelAndRadio({ choice, rubricItem, readOnly }) {
    const rubricValues = useSelector(selector);
    const dispatch = useDispatch();
    
    function updateChoice() {
        const payload = { key: rubricItem, value: choice.key };
        dispatch(setRubricValue(payload));
    }
    
    const isChecked = rubricValues?.[rubricItem] == choice.key;
    
    return <Mui.FormControlLabel value={choice.key} control={<Mui.Radio checked={isChecked} onChange={updateChoice} disabled={readOnly} />} label={choice.label} />;
}

function RubricIsRequired({ missingConfig }) {
    if (missingConfig) { return <></>; }
    
    return <Mui.Typography sx={{ fontSize: "90%" }} tabindex="0">* A grade value is required for all rating items.</Mui.Typography>;
}

function MissingRubricConfiguration({ missingConfig }) {
    const classes = useCommonStyles();
    if (!missingConfig) { return <></>; }
    
    return (
        <>
            <Mui.Typography sx={{ ...classes.errorClass }}>Missing Ratings Configuration</Mui.Typography>
            <Mui.Typography sx={{ marginBottom: "1rem" }}>This section requires a configured ratings set.<br/>Please contact your administrator or email support.</Mui.Typography>
        </>
    );
}